import React, { JSX } from "react";
import PrintTwoToneIcon from "@mui/icons-material/PrintTwoTone";
import { Box, Tooltip } from "@mui/material";
import { useReactToPrint } from "react-to-print";
import Barcode from "react-barcode";
import moment from "moment-timezone";
import { convertADtoBS } from "../../../components/Calendar/functions/calendarFunctions";
import { getDisplayAgeFromDOB } from "../../../helpers/formatters";
import { LabClient, LabTestRecordResultData } from "../../../interfaces/Lab";
import mixpanelAnalytics from "../../../mixpanel-analytics/mixpanelAnalytics";
import EVENT from "../../../mixpanel-analytics/event";
import { PrintTypography } from "../../Stock/StockCreateEdit/BarCodePrint";
import useCurrentResourceCentre from "../../../hooks/useCurrentResourceCentre";

export const generateLisBarcode = (orderId: number): string => String(orderId).padStart(10, "0");

export const parseLisBarcode = (barcode: string): number | null => {
  const match = barcode.trim().match(/^0*(\d+)$/);
  if (!match) return null;
  const id = parseInt(match[1], 10);
  return Number.isNaN(id) ? null : id;
};

const barCodePrintPageStyle = `
  @page { 
    size: 50mm 25mm; 
    margin: 0; 
  }
  @media print {
    .page-break {
      page-break-after: always !important;
      break-after: page !important;
    }
  }
`;

interface PrintBarCodeForLIS {
  labTests: LabTestRecordResultData[];
  client: LabClient;
  barcode: string;
}

const PrintBarCodeForLIS = ({
  labTests,
  client,
  barcode
}: PrintBarCodeForLIS): JSX.Element | null => {
  const currentDateAndTime = moment().format();
  const rc = useCurrentResourceCentre();
  const BSDate = convertADtoBS(moment.utc(currentDateAndTime.slice(0, 10))).formatted;
  const printRef = React.useRef(null);
  const print = useReactToPrint({
    content: () => printRef.current,
    pageStyle: barCodePrintPageStyle,
    onBeforePrint: () => {
      mixpanelAnalytics.track(EVENT.PRINT_LAB_BARCODE, { rcId: rc.id, rcName: rc.name });
    }
  });

  const categoryMap = labTests.reduce((map, item) => {
    const cat = item.category;
    if (!cat) return map;

    if (map.has(cat)) {
      const existing = map.get(cat);
      if (item.name && !existing.names.includes(item.name)) {
        existing.names.push(item.name);
        existing.combinedNames = existing.names.join(", ");
      }
    } else {
      map.set(cat, {
        sampleNumber: cat,
        names: item.name ? [item.name] : [],
        combinedNames: item.name || ""
      });
    }

    return map;
  }, new Map());

  const uniqueCategoryData = Array.from(categoryMap.values());

  const getFullName = (c: Partial<LabClient>): string => `${c?.firstName} ${c?.lastName}`;
  const fullName = getFullName(client);
  if (labTests.length === 0) return null;
  const gender = (g) => {
    if (g === "1") return "M";
    if (g === "2") return "F";
    return "";
  };

  return (
    <Box>
      <Tooltip title="LIS Barcode print" aria-label="barcodeprint">
        <PrintTwoToneIcon
          onClick={print}
          data-testomation="printButton"
          sx={{ cursor: "pointer", marginLeft: "10px", marginTop: "5px" }}
        />
      </Tooltip>

      <Box
        sx={{
          display: "none",
          "@media print": {
            display: "flex !important",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column"
          }
        }}
        ref={printRef}
      >
        {uniqueCategoryData.map((item, index) => (
          <Box
            key={item.sampleNumber}
            display="flex"
            flexDirection="column"
            className={index !== uniqueCategoryData.length - 1 ? "page-break" : undefined}
            sx={{
              "@media print": {
                pageBreakAfter: "always",
                breakAfter: "page"
              }
            }}
          >
            <PrintTypography
              text={`${fullName}${(client.dob || client.gender || "") && ", "}
          ${client.dob ? `${getDisplayAgeFromDOB(client.dob)}` : ""}
          ${client.dob && client.gender ? "/" : ""}
          ${client.gender ? gender(client.gender) : ""}`}
              fontSize="1.8mm"
              maxWidth="45mm"
            />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <Barcode value={barcode} width={1.2} displayValue={false} height={25} margin={0} />
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between"
              }}
            >
              <PrintTypography text={barcode} />
              <PrintTypography text={BSDate} />
            </Box>
            <PrintTypography fontSize="1.7mm" maxWidth="37mm" text={item.combinedNames} />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default PrintBarCodeForLIS;
